import { CHAT_MARKDOWN_COMPONENTS } from './markdown';

import { Button, IconButton, Icons, Textarea } from '@commenda/kakashi';
import { useState } from 'react';
import { Streamdown } from 'streamdown';
import { QuestionBlockHelper } from './question-block';
import { QUESTION_CARD_SECONDARY_BUTTON } from './question-card-buttons';
import { QuestionCardShell } from './QuestionCardShell';

/** The recorded answer is the correction behind this prefix, so the textarea's own cap leaves
 * room for it within the answer limit. */
const CHANGES_REQUESTED_PREFIX = 'Changes requested: ';
const CORRECTION_MAX_LENGTH =
  QuestionBlockHelper.ANSWER_MAX_LENGTH - CHANGES_REQUESTED_PREFIX.length;

interface QuestionPlanCardProps {
  title: string;
  summary?: string;
  /** Markdown, rendered with the chat's own markdown components. */
  body: string;
  isDisabled: boolean;
  /** `chosen` is what the record stores; `text` is what the transcript shows. */
  onAnswer: (answer: { chosen: string; text: string }) => void;
  onClose: () => void;
}

/** The approve-plan card (Figma "Question / Card", Approve plan). The collapse chevron folds the
 * markdown body to the title-plus-summary state; the close X (shell) dismisses without sending —
 * two different controls on purpose. Suggest changes reveals the correction input; the button
 * stays disabled until it carries text, per the admin ResearchPlanReviewCard. The markdown renders
 * through Streamdown directly (not MessageResponse) to keep this module out of ai-elements'
 * import cycle with the fence renderer. */
export function QuestionPlanCard({
  title,
  summary,
  body,
  isDisabled,
  onAnswer,
  onClose,
}: QuestionPlanCardProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCorrectionOpen, setIsCorrectionOpen] = useState(false);
  const [correction, setCorrection] = useState('');
  const trimmedCorrection = correction.trim();

  return (
    <QuestionCardShell
      label="Plan"
      ariaLabel={title}
      onClose={onClose}
      headerControls={
        <IconButton
          size="sm"
          icon={
            isCollapsed ? (
              <Icons.ChevronDown className="size-4" />
            ) : (
              <Icons.ChevronUp className="size-4" />
            )
          }
          aria-label={isCollapsed ? 'Expand plan' : 'Collapse plan'}
          onClick={() => setIsCollapsed((collapsed) => !collapsed)}
        />
      }
      status="You can edit any step"
      actions={
        <>
          <Button
            size="sm"
            variant="secondary"
            className={QUESTION_CARD_SECONDARY_BUTTON}
            disabled={isDisabled}
            onClick={() =>
              onAnswer({
                chosen: 'Skipped',
                text: 'You decide, use your best judgment.',
              })
            }
          >
            Skip
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className={QUESTION_CARD_SECONDARY_BUTTON}
            disabled={isDisabled || (isCorrectionOpen && !trimmedCorrection)}
            onClick={() => {
              if (!isCorrectionOpen) {
                setIsCorrectionOpen(true);
                return;
              }
              onAnswer({
                chosen: `${CHANGES_REQUESTED_PREFIX}${trimmedCorrection}`,
                text: `Please adjust the plan: ${trimmedCorrection}`,
              });
            }}
          >
            Suggest changes
          </Button>
          <Button
            size="sm"
            disabled={isDisabled}
            onClick={() =>
              onAnswer({
                chosen: 'Approved',
                text: 'Approved, go ahead with the plan.',
              })
            }
          >
            Approve plan
          </Button>
        </>
      }
    >
      <div className="flex flex-col">
        <p className="txt-small-medium text-text-primary">{title}</p>
        {summary && (
          <p className="txt-extra-small-regular text-text-secondary">
            {summary}
          </p>
        )}
      </div>
      {!isCollapsed && (
        // Cap the plan detail and scroll it, so a long plan never pushes the actions off screen.
        <div className="max-h-[38vh] overflow-y-auto text-sm text-text-primary">
          <Streamdown
            components={CHAT_MARKDOWN_COMPONENTS}
            mode="static"
            className="[&>*:first-child]:mt-none [&>*:last-child]:mb-none"
          >
            {body}
          </Streamdown>
        </div>
      )}
      {isCorrectionOpen && (
        <Textarea
          value={correction}
          onChange={(event) => setCorrection(event.target.value)}
          placeholder="What should change about the plan?"
          maxLength={CORRECTION_MAX_LENGTH}
          rows={2}
          disabled={isDisabled}
        />
      )}
    </QuestionCardShell>
  );
}
